import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { toast, Toaster } from "sonner";

import RootPage from "@pages/main";
import Catalog from "@pages/catalog";
import Authorization from "@pages/auth";
import Product from "@pages/product";
import Account from "@pages/account";
import useUserData from "@hooks/useUserData";

import Orders from "@pages/admin/orders";
import Products from "@pages/admin/products";
import Chats from "@pages/admin/chats";
import Categories from "@pages/admin/categories";
import Category from "@pages/admin/categories/category";
import ProductAdmin from "@pages/admin/products/product";
import Users from "@pages/admin/users";
import User from "@pages/admin/users/user";
import OrderAdmin from "@pages/admin/orders/order";
import NotFound from "@pages/system/notFound";
import { isMobile } from "react-device-detect";
import Cart from "@pages/cart";
import Chat from "@pages/chat";
import Order from "@pages/account/order";
import Search from "@pages/search";
import Stocks from "@pages/admin/stocks";
import Landing from "@pages/landing";
import axios from "axios";
import Favorites from "@pages/favorites";
import Checkout from "@pages/cart/checkout";
import AdmOrganizations from "@pages/admin/organizations";
import AdmOrganization from "@pages/admin/organizations/organization";
import Notifications from "@pages/admin/notifications";

function App() {
  const { userData } = useUserData();
  const token = localStorage.getItem("authToken");

  useEffect(() => {
    (window as any).receiveDataFromApp = async (tokenMobile: string) => {
      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/user/subscribe-mobile`,
          { token: tokenMobile },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 201) {
          localStorage.setItem("sentMobileToken", tokenMobile);
          // toast.success("Уведомление подписано");
        }
      } catch (error: any) {
        if (error?.response?.status !== 403) {
          toast.error(`${error}`);
          console.error("WEB: Error sending mobile token", error);
        }
      }
    };

    return () => {
      delete (window as any).receiveDataFromApp;
    };
  }, []);

  return (
    <Router basename="/">
      <Toaster
        position="top-center"
        richColors
        expand={false}
        toastOptions={{
          style: { fontSize: 18, marginTop: -10, padding: 10 },
        }}
      />
      <ScrollToTop />
      <Routes>
        <Route path="*" element={<NotFound />} />
        <Route
          path="/"
          element={
            token ? userData.access > 0 ? <Orders /> : <Landing /> : <Landing />
          }
        />
        <Route
          path="/webapp"
          element={
            token ? (
              isMobile ? (
                <RootPage />
              ) : userData.access >= 1 ? (
                <Orders />
              ) : (
                <Landing />
              )
            ) : (
              <Authorization />
            )
          }
        />
        <Route
          path="/webapp/chats"
          element={token ? isMobile ? <Chat /> : <Orders /> : <Authorization />}
        />
        <Route
          path="/webapp/user"
          element={
            token ? isMobile ? <Account /> : <Orders /> : <Authorization />
          }
        />
        <Route
          path="/webapp/cart"
          element={token ? isMobile ? <Cart /> : <Orders /> : <Authorization />}
        />
        <Route
          path="/webapp/checkout"
          element={token ? isMobile ? <Checkout /> : <Orders /> : <Authorization />}
        />
        <Route
          path="/webapp/product/:id"
          element={
            token ? (
              isMobile ? (
                <Product />
              ) : (
                <ProductAdmin />
              )
            ) : (
              <Authorization />
            )
          }
        />
        <Route
          path="/webapp/catalog/:id"
          element={
            token ? isMobile ? <Catalog /> : <Orders /> : <Authorization />
          }
        />
        <Route
          path="/webapp/order/:id"
          element={
            token ? isMobile ? <Order /> : <Orders /> : <Authorization />
          }
        />
        <Route
          path="/webapp/search/:query"
          element={
            token ? isMobile ? <Search /> : <Orders /> : <Authorization />
          }
        />

        <Route
          path="/webapp/favorites"
          element={
            token ? <Favorites /> : <Authorization />
          }
        />

        {userData.access > 0 && (
          <>
            <Route path="/manage" element={<Orders />} />
            <Route path="/manage/orders" element={<Orders />} />
            <Route path="/manage/order/:id" element={<OrderAdmin />} />
            <Route path="/manage/products" element={<Products />} />
            <Route path="/manage/product/:id" element={<ProductAdmin />} />
            <Route path="/manage/users" element={<Users />} />
            <Route path="/manage/user/:id" element={<User />} />
            <Route path="/manage/categories" element={<Categories />} />
            <Route path="/manage/categories/:id" element={<Category />} />
            <Route path="/manage/chats" element={<Chats />} />
            <Route path="/manage/stocks" element={<Stocks />} />
            <Route path="/manage/organizations" element={<AdmOrganizations />} />
            <Route path="/manage/organization/:id" element={<AdmOrganization />} />
            <Route path="/manage/notifications" element={<Notifications />} />
          </>
        )}
      </Routes>
    </Router>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === "POP") {
      return;
    }
    
    const element = document.getElementById("scrollable-content");
    if (element) {
      element.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [pathname, navType]);

  return null;
}

export default App;
